import { ArrowDropDown, ArrowDropUp, Home, Search } from '@mui/icons-material';
import {
  Box,
  Breadcrumbs,
  Button,
  Divider,
  InputAdornment,
  MenuItem,
  MenuList,
  Popover,
  Skeleton,
  TextField,
  Typography,
  lighten,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { ReactNode, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAdminUrlNavigation } from 'src/hooks/useAdminUrlNavigation';
import { useApplicationGroups } from 'src/hooks/useApplicationGroups';
import { useApplications } from 'src/hooks/useApplications';
import useEffectiveApplicationGroupId from 'src/hooks/useEffectiveApplicationGroupId';
import useEffectiveApplicationId from 'src/hooks/useEffectiveApplicationId';
import { useNavigateWithState } from 'src/hooks/useNavigateWithState';
import { useSelectedApplicationGroupId } from 'src/hooks/useSelectedApplicationGroupId';
import { useSelectedApplicationId } from 'src/hooks/useSelectedApplicationId';
import { routeBreadcrumbConfig } from 'src/router/router';
import pages from 'src/router/routes';
import { ALL_SELECTION_ID, isAllSelection } from 'src/utils/selection';

interface BreadcrumbNavProps {
  showHome?: boolean;
  actionControls?: ReactNode;
}

interface BreadcrumbLocationState {
  breadcrumbName?: string;
  entityName?: string;
  name?: string;
  fromUnallocatedDevices?: boolean;
}

const SYSTEM_SECTION_LABELS: Record<string, string> = {
  g: 'Application Groups',
  [pages.applicationGroup.name]: 'Application Groups',
  [pages.accounting.name]: 'Accounting',
  [pages.providers.name]: 'Providers',
  [pages.unallocatedDevices.name]: 'Unallocated Devices',
};

const SYSTEM_SECTION_CRUMBS: Record<string, { label: string; path: string }> = {
  [pages.applicationGroup.name]: {
    label: 'Application Groups',
    path: pages.applicationGroup.path,
  },
  [pages.application.name]: {
    label: 'Applications',
    path: pages.application.path,
  },
  [pages.accounting.name]: {
    label: 'Accounting',
    path: pages.accounting.path,
  },
  [pages.providers.name]: {
    label: 'Providers',
    path: pages.providers.path,
  },
  [pages.unallocatedDevices.name]: {
    label: 'Unallocated Devices',
    path: pages.unallocatedDevices.path,
  },
};

const APP_SCOPED_SECTION_CRUMBS: Record<
  string,
  { label: string; sectionPath: string; legacyPath: string }
> = {
  [pages.accounts.name]: {
    label: 'Accounts',
    sectionPath: pages.accounts.name,
    legacyPath: pages.accounts.path,
  },
  [pages.utility.name]: {
    label: 'Utilities',
    sectionPath: pages.utility.name,
    legacyPath: pages.utility.path,
  },
  [pages.meterpoints.name]: {
    label: 'Meter Points',
    sectionPath: pages.meterpoints.name,
    legacyPath: pages.meterpoints.path,
  },
  [pages.meters.name]: {
    label: 'Meters',
    sectionPath: pages.meters.name,
    legacyPath: pages.meters.path,
  },
  [pages.gateways.name]: {
    label: 'Gateways',
    sectionPath: pages.gateways.name,
    legacyPath: pages.gateways.path,
  },
  [pages.sensors.name]: {
    label: 'Sensors',
    sectionPath: pages.sensors.name,
    legacyPath: pages.sensors.path,
  },
  [pages.zones.name]: {
    label: 'Zones',
    sectionPath: pages.zones.name,
    legacyPath: pages.zones.path,
  },
  [pages.payments.name]: {
    label: 'Payments',
    sectionPath: pages.payments.name,
    legacyPath: pages.payments.path,
  },
  [pages.invoices.name]: {
    label: 'Invoices',
    sectionPath: pages.invoices.name,
    legacyPath: pages.invoices.path,
  },
  [pages.recons.name]: {
    label: 'Recons',
    sectionPath: pages.recons.name,
    legacyPath: pages.recons.path,
  },
  [pages.manualReadings.name]: {
    label: 'Manual Data',
    sectionPath: pages.manualReadings.name,
    legacyPath: pages.manualReadings.path,
  },
  [pages.emailIntegrationLogs.name]: {
    label: 'Email Integration Logs',
    sectionPath: pages.emailIntegrationLogs.name,
    legacyPath: pages.emailIntegrationLogs.path,
  },
};

const GROUP_SCOPED_SECTION_CRUMBS: Record<
  string,
  { label: string; sectionPath: string; legacyPath: string }
> = {
  [pages.reports.name]: {
    label: 'Reports',
    sectionPath: pages.reports.name,
    legacyPath: pages.reports.path,
  },
  [pages.tariffs.name]: {
    label: 'Tariffs',
    sectionPath: pages.tariffs.name,
    legacyPath: pages.tariffs.path,
  },
  [pages.integrations.name]: {
    label: 'Integrations',
    sectionPath: pages.integrations.name,
    legacyPath: pages.integrations.path,
  },
};

const ROUTE_LABELS: Record<string, string> = {
  [pages.dashboard.name]: 'Dashboard',
  [pages.accounts.name]: 'Accounts',
  [pages.meters.name]: 'Meters',
  [pages.meterpoints.name]: 'Meter Points',
  [pages.gateways.name]: 'Gateways',
  [pages.zones.name]: 'Zones',
  [pages.sensors.name]: 'Sensors',
  [pages.utility.name]: 'Utilities',
  [pages.tariffs.name]: 'Tariffs',
  [pages.payments.name]: 'Payments',
  [pages.invoices.name]: 'Invoices',
  [pages.reports.name]: 'Reports',
  [pages.users.name]: 'Users',
  [pages.recons.name]: 'Recons',
  [pages.integrations.name]: 'Integrations',
  [pages.manualReadings.name]: 'Manual Data',
  [pages.emailIntegrationLogs.name]: 'Email Integration Logs',
  'unallocated-meters': 'Unallocated Meters',
  [pages.unallocatedDevices.name]: 'Unallocated Devices',
  [pages.financials.name]: 'Financials',
  [pages.providers.name]: 'Providers',
  [pages.accounting.name]: 'Accounting',
};

const BreadcrumbNav = ({
  showHome = true,
  actionControls,
}: BreadcrumbNavProps) => {
  /* Hooks & Use Effects */
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { navigateWithGroupContext } = useNavigateWithState();
  const {
    isUrlNavigationEnabled,
    neutralDashboardPath,
    getApplicationGroupDashboardPath,
    getApplicationDashboardPath,
    getApplicationGroupsPath,
    getApplicationScopedPath,
    getApplicationsPath,
    getGroupScopedPath,
    getUsersPath,
  } = useAdminUrlNavigation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  /* States */
  const [groupAnchorEl, setGroupAnchorEl] = React.useState<null | HTMLElement>(
    null
  );
  const [appAnchorEl, setAppAnchorEl] = React.useState<null | HTMLElement>(
    null
  );
  const [groupSearchQuery, setGroupSearchQuery] = React.useState('');
  const [appSearchQuery, setAppSearchQuery] = React.useState('');
  const groupSearchInputRef = React.useRef<HTMLInputElement>(null);
  const appSearchInputRef = React.useRef<HTMLInputElement>(null);
  const groupMenuListRef = React.useRef<HTMLUListElement>(null);
  const appMenuListRef = React.useRef<HTMLUListElement>(null);

  const selectedGroupId = useEffectiveApplicationGroupId();
  const selectedAppId = useEffectiveApplicationId();
  const {
    applicationGroups,
    isLoading: isGroupsLoading,
    isFetching: isGroupsFetching,
  } = useApplicationGroups();
  const {
    applications,
    isLoading: isAppsLoading,
    isFetching: isAppsFetching,
  } = useApplications();
  const { setSelectedGroupId } = useSelectedApplicationGroupId();
  const { setSelectedApplicationId } = useSelectedApplicationId();

  const openGroupDropdown = Boolean(groupAnchorEl);
  const openAppDropdown = Boolean(appAnchorEl);
  const groupScopedSections = useMemo(
    () => new Set(['reports', 'tariffs', 'integrations']),
    []
  );
  const breadcrumbLocationState = useMemo(
    () => location.state as BreadcrumbLocationState | null,
    [location.state]
  );

  const isUnallocatedMeterDetailPath = useMemo(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);

    return (
      breadcrumbLocationState?.fromUnallocatedDevices === true &&
      pathSegments[0] === pages.meters.name &&
      pathSegments[1] !== undefined &&
      pathSegments[1] !== 'create'
    );
  }, [breadcrumbLocationState?.fromUnallocatedDevices, location.pathname]);

  const breadcrumbEntityName = useMemo(
    () =>
      breadcrumbLocationState?.breadcrumbName ??
      breadcrumbLocationState?.entityName ??
      breadcrumbLocationState?.name,
    [breadcrumbLocationState]
  );

  /* API Calls */

  /* Functions & Functional Components */

  const availableApplicationGroups = useMemo(
    () =>
      applicationGroups.filter(
        (group) => !isAllSelection(group._id) && group.name !== 'All Groups'
      ),
    [applicationGroups]
  );

  const showGroupsNavigation = useMemo(() => {
    // Show groups navigation if there are multiple groups
    return availableApplicationGroups.length > 1;
  }, [availableApplicationGroups]);

  // Check if "All Groups" is selected and available for the current user.
  const isAllGroupsSelected =
    isAllSelection(selectedGroupId) && showGroupsNavigation;

  const canSelectApplication = useMemo(() => {
    if (isAllGroupsSelected) return false;

    return applications.some(
      (app) => !isAllSelection(app._id) && app.name !== 'All Applications'
    );
  }, [applications, isAllGroupsSelected]);

  const selectedGroup = useMemo(() => {
    if (isAllGroupsSelected) {
      return { _id: ALL_SELECTION_ID, name: 'All Groups' };
    }
    return applicationGroups.find((group) => group._id === selectedGroupId);
  }, [applicationGroups, selectedGroupId, isAllGroupsSelected]);

  const selectedApp = useMemo(() => {
    if (isAllSelection(selectedAppId)) {
      return { _id: ALL_SELECTION_ID, name: 'All Applications' };
    }
    return applications.find((app) => app._id === selectedAppId);
  }, [applications, selectedAppId]);
  const isGroupCrumbLoading =
    isUrlNavigationEnabled &&
    !isAllSelection(selectedGroupId) &&
    !selectedGroup &&
    (isGroupsLoading || isGroupsFetching);
  const isAppCrumbLoading =
    isUrlNavigationEnabled &&
    !isAllGroupsSelected &&
    !isAllSelection(selectedAppId) &&
    !selectedApp &&
    (isAppsLoading || isAppsFetching);

  const filteredGroups = useMemo(() => {
    const groups = groupSearchQuery
      ? availableApplicationGroups.filter((group) =>
          group.name.toLowerCase().includes(groupSearchQuery.toLowerCase())
        )
      : availableApplicationGroups;

    // Add "All Groups" option for users with more than one group
    if (showGroupsNavigation) {
      const allGroupsOption = { _id: ALL_SELECTION_ID, name: 'All Groups' };
      const matchesSearch =
        !groupSearchQuery ||
        'all groups'.includes(groupSearchQuery.toLowerCase());
      if (matchesSearch) {
        return [allGroupsOption, ...groups];
      }
    }

    return groups;
  }, [availableApplicationGroups, groupSearchQuery, showGroupsNavigation]);

  const allApplicationsOption = useMemo(
    () => ({ _id: ALL_SELECTION_ID, name: 'All Applications' }),
    []
  );

  const filteredApplications = useMemo(() => {
    const apps = appSearchQuery
      ? applications.filter((app) =>
          app.name.toLowerCase().includes(appSearchQuery.toLowerCase())
        )
      : applications;

    const hasAllOption = apps.some((app) => isAllSelection(app._id));
    return hasAllOption ? apps : [allApplicationsOption, ...apps];
  }, [applications, appSearchQuery, allApplicationsOption]);

  // Determine if current page should hide parts of the hierarchy based on route configuration
  const hierarchyConfig = useMemo(() => {
    // Get the base path (first segment) from the current pathname
    const pathSegments = location.pathname.split('/').filter(Boolean);
    if (pathSegments.length === 0)
      return {
        hideGroups: false,
        hideApplications: false,
        level: 'app' as const,
      };

    if (isUnallocatedMeterDetailPath) {
      return {
        hideGroups: true,
        hideApplications: true,
        level: 'system' as const,
      };
    }

    if (pathSegments[0] === 'g') {
      const sectionSegment = pathSegments[2];

      if (!pathSegments[1] || pathSegments[1] === 'create') {
        return {
          hideGroups: true,
          hideApplications: true,
          level: 'system' as const,
        };
      }

      if (!sectionSegment) {
        return {
          hideGroups: false,
          hideApplications: true,
          level: 'group' as const,
        };
      }

      if (sectionSegment === 'dashboard') {
        return {
          hideGroups: false,
          hideApplications: false,
          level: 'group' as const,
        };
      }

      if (sectionSegment === 'a') {
        const appSegment = pathSegments[3];
        const groupLevelApplicationSegments = new Set([undefined, 'create']);

        if (groupLevelApplicationSegments.has(appSegment)) {
          return {
            hideGroups: false,
            hideApplications: true,
            level: 'group' as const,
          };
        }

        return {
          hideGroups: false,
          hideApplications: false,
          level: 'app' as const,
        };
      }

      if (
        sectionSegment === 'users' ||
        groupScopedSections.has(sectionSegment)
      ) {
        return {
          hideGroups: false,
          hideApplications: true,
          level: 'group' as const,
        };
      }

      return {
        hideGroups: true,
        hideApplications: true,
        level: 'system' as const,
      };
    }

    // Construct the base path to look up in the config
    const basePath = `/${pathSegments[0]}`;

    // Look up the breadcrumb level for this route
    const level = routeBreadcrumbConfig[basePath] || 'app';

    switch (level) {
      case 'system':
        // System-level pages (e.g., Application Groups): hide both
        return {
          hideGroups: true,
          hideApplications: true,
          level: 'system' as const,
        };
      case 'group':
        // Group-level pages (e.g., Applications, Users, Reports): hide only applications
        return {
          hideGroups: false,
          hideApplications: true,
          level: 'group' as const,
        };
      case 'app':
      default:
        // Application-level pages (default): show full hierarchy
        return {
          hideGroups: false,
          hideApplications: false,
          level: 'app' as const,
        };
    }
  }, [groupScopedSections, isUnallocatedMeterDetailPath, location.pathname]);

  const systemSectionName = useMemo(() => {
    if (hierarchyConfig.level !== 'system') return undefined;

    const pathSegments = location.pathname.split('/').filter(Boolean);
    const pageKey = pathSegments[0];

    if (isUnallocatedMeterDetailPath && pageKey === pages.meters.name) {
      return 'Unallocated Devices';
    }

    return SYSTEM_SECTION_LABELS[pageKey];
  }, [hierarchyConfig.level, isUnallocatedMeterDetailPath, location.pathname]);

  const systemSectionCrumb = useMemo(() => {
    if (hierarchyConfig.level !== 'system') return undefined;

    const pathSegments = location.pathname.split('/').filter(Boolean);
    const pageKey = pathSegments[0];
    const childSegment = pathSegments[1];

    const sectionMap: Record<string, { label: string; path: string }> = {
      g: {
        label: 'Application Groups',
        path: getApplicationGroupsPath(),
      },
      ...SYSTEM_SECTION_CRUMBS,
      ...(isUnallocatedMeterDetailPath
        ? {
            [pages.meters.name]: {
              label: 'Unallocated Devices',
              path: pages.unallocatedDevices.path,
            },
          }
        : {}),
    };

    const section = sectionMap[pageKey];
    if (!section || !childSegment) {
      return undefined;
    }

    return section;
  }, [
    getApplicationGroupsPath,
    hierarchyConfig.level,
    isUnallocatedMeterDetailPath,
    location.pathname,
  ]);

  const appScopedSectionCrumb = useMemo(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const isAppScopedSection =
      pathSegments[0] === 'g' &&
      pathSegments[2] === 'a' &&
      pathSegments[3] &&
      pathSegments[4] &&
      pathSegments[5];

    if (!isAppScopedSection) return undefined;

    const section = APP_SCOPED_SECTION_CRUMBS[pathSegments[4]];
    if (!section) return undefined;

    return {
      label: section.label,
      path: getApplicationScopedPath(
        section.sectionPath,
        section.legacyPath,
        pathSegments[1],
        pathSegments[3]
      ),
    };
  }, [getApplicationScopedPath, location.pathname]);

  const groupScopedSectionCrumb = useMemo(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const groupId = pathSegments[1];
    const sectionSegment = pathSegments[2];
    const childSegment = pathSegments[3];

    if (pathSegments[0] !== 'g' || !groupId || !sectionSegment) {
      return undefined;
    }

    const isApplicationDetailsRoute =
      sectionSegment === 'a' &&
      childSegment &&
      childSegment !== 'create' &&
      !pathSegments[4];
    const isApplicationCreateRoute =
      sectionSegment === 'a' && childSegment === 'create';

    if (isApplicationCreateRoute) {
      return {
        label: 'Applications',
        path: getApplicationsPath(groupId),
      };
    }

    if (isApplicationDetailsRoute) {
      return undefined;
    }

    if (sectionSegment === pages.users.name && childSegment) {
      return {
        label: 'Users',
        path: getUsersPath(groupId),
      };
    }

    const section = GROUP_SCOPED_SECTION_CRUMBS[sectionSegment];
    if (!section || !childSegment) return undefined;

    return {
      label: section.label,
      path: getGroupScopedPath(
        section.sectionPath,
        section.legacyPath,
        groupId
      ),
    };
  }, [
    getApplicationsPath,
    getGroupScopedPath,
    getUsersPath,
    location.pathname,
  ]);

  const shouldShowCurrentPageCrumb = useMemo(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);

    const isGroupDetailsRoute =
      pathSegments[0] === 'g' && pathSegments[1] && !pathSegments[2];
    const isApplicationDetailsRoute =
      pathSegments[0] === 'g' &&
      pathSegments[1] &&
      pathSegments[2] === 'a' &&
      pathSegments[3] &&
      !pathSegments[4];

    return !(isGroupDetailsRoute || isApplicationDetailsRoute);
  }, [location.pathname]);

  const currentPageName = useMemo(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    if (pathSegments.length === 0 || pathSegments[0] === 'home')
      return 'Dashboard';

    // Handle special cases
    if (pathSegments[0] === 'g') {
      if (pathSegments[1] === 'create') return 'Create Group';
      if (!pathSegments[1]) return 'Application Groups';
      if (!pathSegments[2]) return breadcrumbEntityName ?? 'Item';
      if (pathSegments[2] === 'dashboard') return 'Dashboard';

      if (pathSegments[2] === 'a') {
        if (pathSegments[3] === 'create') return 'Create Application';
        if (pathSegments[4] === 'dashboard') return 'Dashboard';
        if (pathSegments[4]) {
          const sectionName = ROUTE_LABELS[pathSegments[4]];

          if (!sectionName) return breadcrumbEntityName ?? 'Item';

          if (pathSegments[5] === 'create') {
            return 'Create';
          }

          if (pathSegments[5] === 'financials') return 'Financials';
          if (pathSegments[5] === 'whitelists') return 'Whitelists';
          if (pathSegments[5] === 'jobs') return 'Meter Jobs';

          if (pathSegments[5]) {
            return breadcrumbEntityName ?? 'Item';
          }

          return sectionName;
        }
        if (pathSegments[3]) return breadcrumbEntityName ?? 'Item';
        return 'Applications';
      }

      if (pathSegments[2] === pages.users.name) {
        if (pathSegments[3] === 'create') return 'Create User';
        if (pathSegments[3]) return breadcrumbEntityName ?? 'Item';
        return 'Users';
      }

      if (pathSegments[2] === pages.reports.name) return 'Reports';

      if (pathSegments[2] === pages.integrations.name) return 'Integrations';

      if (pathSegments[2] === pages.tariffs.name) {
        if (pathSegments[3] === 'create') return 'Create Tariff';
        if (pathSegments[3]) return breadcrumbEntityName ?? 'Item';
        return 'Tariffs';
      }
    }

    if (pathSegments[0] === pages.applicationGroup.name) {
      if (pathSegments[1] === 'create') return 'Create Group';
      if (pathSegments[1]) return breadcrumbEntityName ?? 'Item';
      return 'Application Groups';
    }

    if (pathSegments[0] === pages.application.name) {
      if (pathSegments[1] === 'create') return 'Create Application';
      if (pathSegments[1]) return breadcrumbEntityName ?? 'Item';
      return 'Applications';
    }

    // Map other routes
    const pageKey = pathSegments[0];
    const baseName = ROUTE_LABELS[pageKey] || pageKey;

    // Check for subpages
    if (pageKey === pages.accounting.name && pathSegments[1] === 'accounts') {
      return breadcrumbEntityName ?? 'Item';
    }
    if (pathSegments[1] === 'create') return `Create ${baseName.slice(0, -1)}`;
    if (pathSegments[1] && pathSegments[1] !== 'create') {
      return breadcrumbEntityName ?? 'Item';
    }

    return baseName;
  }, [breadcrumbEntityName, location.pathname]);

  const handleGroupClick = (event: React.MouseEvent<HTMLElement>) => {
    setGroupAnchorEl(event.currentTarget);
    setGroupSearchQuery('');
  };

  const handleAppClick = (event: React.MouseEvent<HTMLElement>) => {
    setAppAnchorEl(event.currentTarget);
    setAppSearchQuery('');
  };

  const handleGroupClose = () => {
    setGroupAnchorEl(null);
    setGroupSearchQuery('');
  };

  const handleAppClose = () => {
    setAppAnchorEl(null);
    setAppSearchQuery('');
  };

  const handleGroupSelect = (groupId: string) => {
    handleGroupClose();
    if (isAllSelection(groupId)) {
      navigateWithGroupContext(ALL_SELECTION_ID, neutralDashboardPath);
    } else {
      setSelectedGroupId(groupId);
      navigate(getApplicationGroupDashboardPath(groupId));
    }
  };

  const handleAppSelect = (appId: string) => {
    setSelectedApplicationId(appId);
    handleAppClose();
    navigate(
      isAllSelection(appId)
        ? getApplicationGroupDashboardPath(selectedGroupId)
        : getApplicationDashboardPath(appId, selectedGroupId)
    );
  };

  const handleHomeClick = () => {
    navigate(neutralDashboardPath);
  };

  const handleGroupsClick = () => {
    navigateWithGroupContext(ALL_SELECTION_ID, neutralDashboardPath);
  };

  const renderCrumbSkeleton = (width: number) => (
    <Box
      sx={{
        px: 1,
        py: 0.25,
        borderRadius: theme.tokens.borderRadius.full,
      }}
    >
      <Skeleton
        variant='rounded'
        width={width}
        height={18}
        sx={{ borderRadius: theme.tokens.borderRadius.full }}
      />
    </Box>
  );

  const focusMenuEdge = (
    menuListRef: React.RefObject<HTMLUListElement>,
    edge: 'first' | 'last'
  ) => {
    const items = menuListRef.current?.querySelectorAll<HTMLElement>(
      'li[role="menuitem"]'
    );
    if (!items || items.length === 0) return;
    const target = edge === 'first' ? items[0] : items[items.length - 1];
    target?.focus();
  };

  const handleGroupSearchKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      focusMenuEdge(groupMenuListRef, 'first');
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      focusMenuEdge(groupMenuListRef, 'last');
    }
  };

  const handleAppSearchKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      focusMenuEdge(appMenuListRef, 'first');
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      focusMenuEdge(appMenuListRef, 'last');
    }
  };

  useEffect(() => {
    if (!openGroupDropdown) return undefined;
    const timer = window.setTimeout(() => {
      groupSearchInputRef.current?.focus();
      groupSearchInputRef.current?.select();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [openGroupDropdown]);

  useEffect(() => {
    if (!openAppDropdown) return undefined;
    const timer = window.setTimeout(() => {
      appSearchInputRef.current?.focus();
      appSearchInputRef.current?.select();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [openAppDropdown]);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'top',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 2,
        minHeight: '48px',
        marginTop: 1.5,
        px: isMobile ? 2 : 6,
        py: 1,
      }}
    >
      <Breadcrumbs
        separator='/'
        sx={{
          '& .MuiBreadcrumbs-separator': {
            color: theme.tokens.colors.mutedForeground,
            mx: 0,
            mt: 0,
          },
        }}
      >
        {showHome && (
          <Button
            onClick={handleHomeClick}
            sx={{
              minWidth: 'auto',
              px: 0.75,
              py: 0,
              mt: -0.4,
              borderRadius: theme.tokens.borderRadius.full,
              color: theme.tokens.colors.mutedForeground,
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: lighten(theme.tokens.colors.foreground, 0.93),
              },
            }}
          >
            <Home sx={{ height: 22, width: 20 }} />
          </Button>
        )}

        {hierarchyConfig.level === 'system' && (
          <Box
            sx={{
              px: 1,
              py: 0,
              borderRadius: theme.tokens.borderRadius.full,
            }}
          >
            <Typography
              variant='body2'
              fontWeight={500}
              sx={{
                color: theme.tokens.colors.mutedForeground,
              }}
            >
              Admin
            </Typography>
          </Box>
        )}

        {systemSectionCrumb && (
          <Button
            onClick={() => navigate(systemSectionCrumb.path)}
            sx={{
              px: 1,
              py: 0,
              borderRadius: theme.tokens.borderRadius.full,
              textTransform: 'none',
              color: theme.tokens.colors.mutedForeground,
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: lighten(theme.tokens.colors.foreground, 0.93),
              },
            }}
          >
            <Typography variant='body2' fontWeight={500}>
              {systemSectionCrumb.label}
            </Typography>
          </Button>
        )}

        {!systemSectionCrumb &&
          hierarchyConfig.level === 'system' &&
          systemSectionName &&
          systemSectionName !== currentPageName && (
            <Box
              sx={{
                px: 1,
                py: 0,
                borderRadius: theme.tokens.borderRadius.full,
              }}
            >
              <Typography
                variant='body2'
                fontWeight={500}
                sx={{
                  color: theme.tokens.colors.mutedForeground,
                }}
              >
                {systemSectionName}
              </Typography>
            </Box>
          )}

        {/* Groups Link - Always show, clickable only if multiple groups */}
        {!hierarchyConfig.hideGroups && showGroupsNavigation && (
          <Button
            onClick={handleGroupsClick}
            sx={{
              px: 1,
              py: 0,
              borderRadius: theme.tokens.borderRadius.full,
              textTransform: 'none',
              color: theme.tokens.colors.mutedForeground,
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: lighten(theme.tokens.colors.foreground, 0.93),
              },
            }}
          >
            <Typography variant='body2' fontWeight={500}>
              Groups
            </Typography>
          </Button>
        )}

        {!hierarchyConfig.hideGroups && !showGroupsNavigation && (
          <Box
            sx={{
              px: 1,
              py: 0,
              borderRadius: theme.tokens.borderRadius.full,
            }}
          >
            <Typography
              variant='body2'
              fontWeight={500}
              sx={{
                color: theme.tokens.colors.mutedForeground,
              }}
            >
              Groups
            </Typography>
          </Box>
        )}

        {/* Application Group Dropdown or Static Text */}
        {!hierarchyConfig.hideGroups &&
          isGroupCrumbLoading &&
          renderCrumbSkeleton(120)}

        {!hierarchyConfig.hideGroups &&
          !isGroupCrumbLoading &&
          showGroupsNavigation && (
            <Box>
              <Button
                onClick={handleGroupClick}
                endIcon={
                  openGroupDropdown ? (
                    <ArrowDropUp sx={{ marginLeft: -1 }} />
                  ) : (
                    <ArrowDropDown sx={{ marginLeft: -1 }} />
                  )
                }
                sx={{
                  px: 1,
                  py: 0,
                  borderRadius: theme.tokens.borderRadius.full,
                  textTransform: 'none',
                  color: theme.tokens.colors.foreground,
                  backgroundColor: 'transparent',
                  '&:hover': {
                    backgroundColor: lighten(
                      theme.tokens.colors.foreground,
                      0.93
                    ),
                  },
                }}
              >
                <Typography
                  variant='body2'
                  fontWeight={600}
                  sx={{
                    maxWidth: '200px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {selectedGroup?.name || 'Select Group'}
                </Typography>
              </Button>
              <Popover
                open={openGroupDropdown}
                anchorEl={groupAnchorEl}
                onClose={handleGroupClose}
                disableScrollLock
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                sx={{ mt: `${theme.tokens.spacing.xs}px` }}
              >
                <Box sx={{ p: 1.5 }}>
                  <TextField
                    fullWidth
                    size='small'
                    placeholder='Search groups...'
                    value={groupSearchQuery}
                    onChange={(e) => setGroupSearchQuery(e.target.value)}
                    onKeyDown={handleGroupSearchKeyDown}
                    inputRef={groupSearchInputRef}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position='start'>
                          <Search fontSize='small' />
                        </InputAdornment>
                      ),
                    }}
                    autoFocus
                  />
                </Box>
                <Divider />
                <MenuList
                  ref={groupMenuListRef}
                  sx={{
                    maxHeight: '300px',
                    overflowY: 'auto',
                    padding: `${theme.tokens.spacing.xs}px`,
                    '& > :not(:last-child)': {
                      marginBottom: `${theme.tokens.spacing.xs}px`,
                    },
                  }}
                >
                  {filteredGroups.length === 0 ? (
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant='body2' color='text.secondary'>
                        No groups found
                      </Typography>
                    </Box>
                  ) : (
                    filteredGroups.map((group) => (
                      <MenuItem
                        key={group._id}
                        selected={group._id === selectedGroupId}
                        onClick={() => handleGroupSelect(group._id)}
                        sx={{ py: 1, px: 2 }}
                      >
                        <Typography variant='body2'>{group.name}</Typography>
                      </MenuItem>
                    ))
                  )}
                </MenuList>
              </Popover>
            </Box>
          )}

        {!hierarchyConfig.hideGroups &&
          !isGroupCrumbLoading &&
          !showGroupsNavigation && (
            /* Show as static text if only one group */
            <Box
              sx={{
                px: 1,
                py: 0,
                borderRadius: theme.tokens.borderRadius.full,
              }}
            >
              <Typography
                variant='body2'
                fontWeight={600}
                sx={{
                  maxWidth: '200px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  color: theme.tokens.colors.foreground,
                }}
              >
                {selectedGroup?.name || 'Select Group'}
              </Typography>
            </Box>
          )}

        {/* Applications label */}
        {/* Hide when "All Groups" is selected */}
        {!hierarchyConfig.hideApplications && !isAllGroupsSelected && (
          <Box
            sx={{
              px: 1,
              py: 0,
              borderRadius: theme.tokens.borderRadius.full,
            }}
          >
            <Typography
              variant='body2'
              fontWeight={500}
              sx={{
                color: theme.tokens.colors.mutedForeground,
              }}
            >
              Applications
            </Typography>
          </Box>
        )}

        {!hierarchyConfig.hideApplications &&
          !isAllGroupsSelected &&
          isAppCrumbLoading &&
          renderCrumbSkeleton(140)}

        {/* Application Dropdown or Static Text */}
        {/* Hide when "All Groups" is selected */}
        {!hierarchyConfig.hideApplications &&
          !isAllGroupsSelected &&
          !isAppCrumbLoading &&
          selectedApp &&
          canSelectApplication && (
            <Box>
              <Button
                onClick={handleAppClick}
                endIcon={
                  openAppDropdown ? (
                    <ArrowDropUp sx={{ marginLeft: -1 }} />
                  ) : (
                    <ArrowDropDown sx={{ marginLeft: -1 }} />
                  )
                }
                sx={{
                  px: 1,
                  py: 0,
                  borderRadius: theme.tokens.borderRadius.full,
                  textTransform: 'none',
                  color: theme.tokens.colors.foreground,
                  backgroundColor: 'transparent',
                  '&:hover': {
                    backgroundColor: lighten(
                      theme.tokens.colors.foreground,
                      0.93
                    ),
                  },
                }}
              >
                <Typography
                  variant='body2'
                  fontWeight={600}
                  sx={{
                    maxWidth: '200px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {selectedApp.name}
                </Typography>
              </Button>
              <Popover
                open={openAppDropdown}
                anchorEl={appAnchorEl}
                onClose={handleAppClose}
                disableScrollLock
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                sx={{ mt: `${theme.tokens.spacing.xs}px` }}
              >
                <Box sx={{ p: 1.5 }}>
                  <TextField
                    fullWidth
                    size='small'
                    placeholder='Search applications...'
                    value={appSearchQuery}
                    onChange={(e) => setAppSearchQuery(e.target.value)}
                    onKeyDown={handleAppSearchKeyDown}
                    inputRef={appSearchInputRef}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position='start'>
                          <Search fontSize='small' />
                        </InputAdornment>
                      ),
                    }}
                    autoFocus
                  />
                </Box>
                <Divider />
                <MenuList
                  ref={appMenuListRef}
                  sx={{
                    maxHeight: '300px',
                    overflowY: 'auto',
                    padding: `${theme.tokens.spacing.xs}px`,
                    '& > :not(:last-child)': {
                      marginBottom: `${theme.tokens.spacing.xs}px`,
                    },
                  }}
                >
                  {filteredApplications.length === 0 ? (
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant='body2' color='text.secondary'>
                        No applications found
                      </Typography>
                    </Box>
                  ) : (
                    filteredApplications.map((app) => (
                      <MenuItem
                        key={app._id}
                        selected={app._id === selectedAppId}
                        onClick={() => handleAppSelect(app._id)}
                        sx={{ py: 1, px: 2 }}
                      >
                        <Typography variant='body2'>{app.name}</Typography>
                      </MenuItem>
                    ))
                  )}
                </MenuList>
              </Popover>
            </Box>
          )}

        {!hierarchyConfig.hideApplications &&
          !isAllGroupsSelected &&
          !isAppCrumbLoading &&
          selectedApp &&
          !canSelectApplication && (
            /* Show as static text when there are no concrete applications */
            <Box
              sx={{
                px: 1,
                py: 0,
                borderRadius: theme.tokens.borderRadius.full,
              }}
            >
              <Typography
                variant='body2'
                fontWeight={600}
                sx={{
                  maxWidth: '200px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  color: theme.tokens.colors.foreground,
                }}
              >
                {selectedApp.name}
              </Typography>
            </Box>
          )}

        {groupScopedSectionCrumb && (
          <Button
            onClick={() => navigate(groupScopedSectionCrumb.path)}
            sx={{
              px: 1,
              py: 0,
              borderRadius: theme.tokens.borderRadius.full,
              textTransform: 'none',
              color: theme.tokens.colors.mutedForeground,
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: lighten(theme.tokens.colors.foreground, 0.93),
              },
            }}
          >
            <Typography variant='body2' fontWeight={500}>
              {groupScopedSectionCrumb.label}
            </Typography>
          </Button>
        )}

        {appScopedSectionCrumb && (
          <Button
            onClick={() => navigate(appScopedSectionCrumb.path)}
            sx={{
              px: 1,
              py: 0,
              borderRadius: theme.tokens.borderRadius.full,
              textTransform: 'none',
              color: theme.tokens.colors.mutedForeground,
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: lighten(theme.tokens.colors.foreground, 0.93),
              },
            }}
          >
            <Typography variant='body2' fontWeight={500}>
              {appScopedSectionCrumb.label}
            </Typography>
          </Button>
        )}

        {shouldShowCurrentPageCrumb && (
          <Box
            sx={{
              px: 1,
              py: 0.5,
              borderRadius: theme.tokens.borderRadius.full,
            }}
          >
            <Typography
              variant='body2'
              sx={{
                maxWidth: '300px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontWeight: theme.tokens.typography.fontWeight.regular,
                color: theme.tokens.colors.mutedForeground,
              }}
            >
              {currentPageName}
            </Typography>
          </Box>
        )}
      </Breadcrumbs>

      {/* Action Controls Area */}
      {actionControls && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            ml: isMobile ? 1 : 2,
          }}
        >
          {actionControls}
        </Box>
      )}
    </Box>
  );
};

export default BreadcrumbNav;
